/**
 * A score played into a file instead of the speakers, for the audition's audio downloads.
 *
 * It is the panel's own playing, not a second one: the same schedule, the same player and
 * the same sampled instruments, handed an offline context that runs as fast as it can
 * compute rather than in real time. The player is told to look ahead over the whole piece,
 * because nothing moves an offline clock until it is rendered, so every note is laid down
 * before rendering starts.
 */

import { createPlayer, scheduleOf } from "@app/score-editor";
import type { PlayerOptions, Score, ScheduleOptions } from "@app/score-editor";
import type { SampledEngine } from "./sampler";
import { RING } from "./sampler-room";
import { soundsOf } from "./use-sampled-engine";
import { wavOf } from "./wav";

/**
 * Seconds kept after the last note ends, so the file does not cut: a second for the last
 * notes to be let go, and then as long as the hall rings after them.
 */
const TAIL = 1 + RING;

/** Channels written: the two the speakers are given. */
const CHANNELS = 2;

/** The files a score can be played into: what each is for decides how it is written. */
export type AudioFormat = "mp3" | "wav";

/**
 * Samples a second of the MP3: CD rate, which every player reads and which is one of the
 * rates an MP3 can be written at, where the speakers' own rate (96 kHz on some interfaces)
 * need not be. An offline context renders at whatever rate it is given.
 */
const MP3_RATE = 44_100;

/**
 * Kilobits a second of the MP3. It is made to be sent and listened to, so it is small -- a
 * minute and a half is about 2 MB -- but above the 128 the instruments' own recordings are
 * stored at, so a second encoding does not take away what the first one kept.
 */
const MP3_KBPS = 192;

/** `score` played on `engine`'s instruments with `options`, at `rate` samples a second. */
async function render(
  engine: SampledEngine,
  score: Score,
  options: ScheduleOptions & PlayerOptions,
  rate: number,
): Promise<AudioBuffer> {
  const schedule = scheduleOf(score, options);
  await engine.load(soundsOf(schedule));
  const end = schedule.notes.reduce(
    (last, note) => Math.max(last, note.start + note.duration),
    schedule.seconds,
  );
  const context = new OfflineAudioContext(CHANNELS, Math.ceil((end + TAIL) * rate), rate);
  const player = createPlayer(engine.into(context), schedule, {
    ...options,
    lookahead: Number.POSITIVE_INFINITY,
  });
  player.play(0);
  return context.startRendering();
}

/**
 * `score` played on `engine`'s instruments with `options`, as the bytes of a file in
 * `format`: a WAV exact at the speakers' own rate, or an MP3 small enough to send.
 */
export async function renderAudio(
  engine: SampledEngine,
  score: Score,
  options: ScheduleOptions & PlayerOptions,
  format: AudioFormat,
): Promise<Uint8Array> {
  if (format === "wav") return wavOf(await render(engine, score, options, engine.sampleRate));
  // Loaded when an MP3 is asked for, not with the page: the encoder is 170 kB (59 kB gzipped)
  // that no one who only listens needs.
  const [{ encodeMp3 }, sound] = await Promise.all([
    import("./mp3"),
    render(engine, score, options, MP3_RATE),
  ]);
  const channels: Float32Array[] = [];
  for (let at = 0; at < sound.numberOfChannels; at += 1) channels.push(sound.getChannelData(at));
  return encodeMp3(channels, sound.sampleRate, MP3_KBPS);
}
